import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Printer, RotateCcw } from "lucide-react";
import WorksheetViewer from "@/components/worksheets/WorksheetViewer";
import WorksheetTypeSelector from "@/components/worksheets/WorksheetTypeSelector";
import { generateWorksheet } from "@/services/worksheetGeneratorService";
import type { Worksheet } from "@/types/worksheet";
import { useToast } from "@/hooks/use-toast";

type Spec = {
  grade: string;
  subject?: string | null;
  type?: "puzzle" | "matching" | "sorting" | null;
  autoGenerate?: boolean;
};

export function InlineWorksheet({ spec }: { spec: Spec }) {
  const { toast } = useToast();
  const [worksheet, setWorksheet] = useState<Worksheet | null>(null);
  const [busy, setBusy] = useState(false);

  const build = async (type: string, subject: string, _topic: string) => {
    setBusy(true);
    try {
      const g = await generateWorksheet({
        type: type as "puzzle" | "matching" | "sorting",
        grade: spec.grade,
        subject,
        difficulty: "easy",
      });
      setWorksheet({
        id: crypto.randomUUID(),
        title: `${subject} ${type.charAt(0).toUpperCase() + type.slice(1)} Activity`,
        type: {
          id: type,
          name: `${type.charAt(0).toUpperCase() + type.slice(1)} Games`,
          description: `Fun ${type} activities`,
          icon: type,
          category: type as any,
          minGrade: 1,
          maxGrade: 9,
        },
        grade: spec.grade,
        subject,
        content: g.content,
        difficulty: "easy",
        createdAt: new Date().toISOString(),
      });
    } catch (e: any) {
      toast({ title: "Couldn't generate worksheet", description: e?.message ?? "", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (spec.autoGenerate && spec.type && spec.subject && !worksheet && !busy) {
      void build(spec.type, spec.subject, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (busy) {
    return (
      <Card><CardContent className="p-4 flex items-center gap-2 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Generating worksheet…
      </CardContent></Card>
    );
  }

  if (worksheet) {
    return (
      <Card>
        <CardContent className="p-3 space-y-2">
          <div className="flex justify-between items-center gap-2 flex-wrap">
            <div className="text-sm font-medium">{worksheet.title}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => window.print()}>
                <Printer className="h-4 w-4 mr-1" /> Print
              </Button>
              <Button size="sm" variant="outline" onClick={() => setWorksheet(null)}>
                <RotateCcw className="h-4 w-4 mr-1" /> New
              </Button>
            </div>
          </div>
          <WorksheetViewer worksheet={worksheet} onBack={() => setWorksheet(null)} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-sm font-medium mb-3">
          Worksheets · Grade {spec.grade}{spec.subject ? ` · ${spec.subject}` : ""}
        </div>
        <WorksheetTypeSelector grade={spec.grade} onTypeSelect={build} isGenerating={busy} />
      </CardContent>
    </Card>
  );
}
